import type {
  CodeProvenanceKind,
  CodeType,
  TransactionCode,
  TransactionProvider,
  TransactionProviderRoleId
} from '../../../project_starburst/interface/transactionCodes';
import type { TransactionItem, TransactionsData } from '../../../project_starburst/interface/transactions';

export type ProcessInstanceGate = {
  readonly processInstance?: AmplifyAPI.ProcessInstance | null;
  readonly revision?: number;
  readonly descriptions?: CMxAPI.CodeResponse;
};

const text = (value: unknown): string => (typeof value === 'string' ? value : '');
const isDefined = <T>(value: T | undefined): value is T => value !== undefined;

export const isHiddenDiagnosis = (diagnosis: { readonly shared?: boolean; readonly active?: boolean }): boolean =>
  diagnosis.shared === true && diagnosis.active !== true;

export const isUsableCode = (code: unknown): code is string =>
  typeof code === 'string' && code.trim() !== '';

const compareInvoices = (a: AmplifyAPI.Invoice, b: AmplifyAPI.Invoice): number => {
  const dateA = text(a.date_of_service);
  const dateB = text(b.date_of_service);
  if (dateA !== dateB) {
    if (dateA === '') return 1;
    if (dateB === '') return -1;
    return dateA < dateB ? -1 : 1;
  }
  const providerA = text(a.billing_provider_name).toLowerCase();
  const providerB = text(b.billing_provider_name).toLowerCase();
  if (providerA !== providerB) return providerA < providerB ? -1 : 1;
  const guidA = text(a.guid);
  const guidB = text(b.guid);
  if (guidA === guidB) return 0;
  return guidA < guidB ? -1 : 1;
};

const sequenceOf = (transaction: AmplifyAPI.Transaction): number =>
  typeof transaction.sequence_number === 'number' ? transaction.sequence_number : Number.MAX_SAFE_INTEGER;

const hasProcedure = (transaction: AmplifyAPI.Transaction): boolean =>
  isUsableCode(transaction.procedure?.procedure_code);

export function codeListsOf(transaction: AmplifyAPI.Transaction): {
  procedure: AmplifyAPI.ProcedureCode | null;
  modifiers: readonly AmplifyAPI.ModifierCode[];
  diagnoses: readonly AmplifyAPI.DiagnosisCode[];
} {
  const procedure = transaction.procedure !== null && typeof transaction.procedure === 'object'
    ? transaction.procedure
    : null;
  const modifierSource = Array.isArray(procedure?.modifier_codes) && procedure.modifier_codes.length > 0
    ? procedure.modifier_codes
    : Array.isArray(transaction.modifiers) ? transaction.modifiers : [];
  const modifiers = modifierSource.filter((entry) => entry !== null && typeof entry === 'object');
  const diagnoses = Array.isArray(transaction.diagnoses)
    ? transaction.diagnoses.filter((entry) => entry !== null && typeof entry === 'object')
    : [];
  return { procedure, modifiers, diagnoses };
}

const PROVENANCE: Record<string, CodeProvenanceKind> = { PREDICTION: 'cmx', CLINICAL: 'clinical' };

const provenanceOf = (list: readonly CMxCommonApp.CodeProvenance[] | undefined): CodeProvenanceKind[] => {
  const kinds: CodeProvenanceKind[] = [];
  (Array.isArray(list) ? list : []).forEach((entry) => {
    const kind = entry?.artifact_type ? PROVENANCE[entry.artifact_type] : undefined;
    if (kind && !kinds.includes(kind)) kinds.push(kind);
  });
  return kinds;
};

const descriptionOf = (descriptions: CMxAPI.CodeResponse | undefined, code: string): string | undefined => {
  const entry = descriptions?.[code] as { descriptor?: string; description?: string; shortDescription?: string } | undefined;
  const value = entry?.descriptor || entry?.description || entry?.shortDescription;
  return typeof value === 'string' && value !== '' ? value : undefined;
};

const toCode = (
  transactionId: string,
  codeType: CodeType,
  code: unknown,
  sequence: unknown,
  provenances: readonly CMxCommonApp.CodeProvenance[] | undefined,
  descriptions: CMxAPI.CodeResponse | undefined
): TransactionCode | undefined => {
  if (!isUsableCode(code)) return undefined;
  const seq = typeof sequence === 'number' ? sequence : 0;
  return {
    id: `${transactionId}:${codeType}:${seq}:${code}`,
    codeType,
    code,
    description: descriptionOf(descriptions, code),
    provenance: provenanceOf(provenances),
    reviewState: 'review',
    sequence: seq
  };
};

const bySequence = <T extends { sequence: number }>(a: T, b: T): number => a.sequence - b.sequence;

const ROLES: readonly { id: TransactionProviderRoleId; label: string; prefix: string }[] = [
  { id: 'billing', label: 'Billing', prefix: 'billing_provider' },
  { id: 'performing', label: 'Performing', prefix: 'performing_provider' },
  { id: 'ordering', label: 'Ordering', prefix: 'ordering_provider' },
  { id: 'referring', label: 'Referring', prefix: 'referring_provider' }
];

const providersOf = (invoice: AmplifyAPI.Invoice): TransactionProvider[] => {
  const record = invoice as unknown as Record<string, unknown>;
  const result: TransactionProvider[] = [];
  ROLES.forEach(({ id, label, prefix }) => {
    const name = record[`${prefix}_name`];
    if (typeof name !== 'string' || name.trim() === '') return;
    const number = (record[`${prefix}_number`] as { number?: string } | undefined)?.number;
    const key = typeof number === 'string' && number !== '' ? number : name;
    const credentials = record[`${prefix}_credentials`];
    const providerType = record[`${prefix}_type`];
    const existing = result.find((provider) => provider.id === key);
    const role = { id, label };
    if (existing) {
      result[result.indexOf(existing)] = { ...existing, roles: [...existing.roles, role] };
      return;
    }
    result.push({
      id: key,
      name,
      credentials: typeof credentials === 'string' && credentials !== '' ? credentials : undefined,
      providerType: typeof providerType === 'string' && providerType !== '' ? providerType : undefined,
      roles: [role]
    });
  });
  return result;
};

const liveTransactions = (invoice: AmplifyAPI.Invoice, descriptions: CMxAPI.CodeResponse | undefined): TransactionItem[] => {
  const transactions = Array.isArray(invoice.transactions) ? invoice.transactions : [];
  return transactions
    .map((transaction, position) => ({ transaction, position }))
    .filter(({ transaction }) => transaction && transaction.deleted !== true && typeof transaction.guid === 'string')
    .sort((a, b) => sequenceOf(a.transaction) - sequenceOf(b.transaction) || a.position - b.position)
    .map(({ transaction }) => {
      const { procedure, modifiers, diagnoses } = codeListsOf(transaction);
      const procedureCode = procedure
        ? toCode(transaction.guid, 'CPT', procedure.procedure_code, procedure.sequence_number, procedure.code_provenances, descriptions)
        : undefined;
      const item: TransactionItem = {
        id: transaction.guid,
        invoiceId: text(invoice.guid),
        ...(procedureCode && { procedure: procedureCode }),
        modifiers: modifiers.map((modifier) => toCode(transaction.guid, 'MODIFIER', modifier.modifier_code, modifier.sequence_number, modifier.code_provenances, descriptions)).filter(isDefined).sort(bySequence),
        diagnoses: diagnoses.filter((diagnosis) => !isHiddenDiagnosis(diagnosis)).map((diagnosis) => toCode(transaction.guid, 'ICD', diagnosis.diagnosis_code, diagnosis.sequence_number, diagnosis.code_provenances, descriptions)).filter(isDefined).sort(bySequence),
        providers: providersOf(invoice)
      };
      return hasProcedure(transaction) ? item : { ...item, needsProcedure: true };
    });
};

export function transactionsFromProcessInstance(gate: ProcessInstanceGate): TransactionsData {
  if (gate.processInstance === undefined) return { status: 'loading', transactions: [] };
  if (gate.processInstance === null) return { status: 'failed', transactions: [] };
  const invoices = Array.isArray(gate.processInstance.invoices) ? gate.processInstance.invoices : [];
  const transactions = invoices
    .filter((invoice) => invoice && invoice.deleted !== true)
    .slice()
    .sort(compareInvoices)
    .flatMap((invoice) => liveTransactions(invoice, gate.descriptions));
  return { status: 'ready', transactions };
}
